import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { TrashIcon, KeyIcon, CameraIcon } from './Icons';
import ChangePasswordModal from './ChangePasswordModal';
import SelectMenu from './SelectMenu';
import AuditLog from './AuditLog';
import { getKnownAgents } from './callUtils';

const PERMISSION_LABELS = {
  overview: 'Overview',
  mydashboard: 'My Dashboard',
  incoming: 'Incoming Calls',
  ivrpresses: 'IVR Press Stats',
  dialpad: 'Direct Call',
  agents: 'Agents',
  trends: 'Trends',
  query: 'Query',
  followup: 'Follow Up',
  dailyorders: 'Daily Orders',
  rtostockedin: 'RTO Stocked In',
  orderqueries: 'Order Queries',
  abandonedcarts: 'Abandoned Carts',
  whatsapp: 'WhatsApp',
  email: 'Email',
  international: 'International Orders',
  international_logistics: 'International: Logistics',
  international_packing: 'International: Packing',
  international_cancel: 'International: Cancel Order',
  international_admin: 'International: Admin',
  paymentlinks: 'Payment Links',
  backendpayments: 'Backend Payments',
  damagedproducts: 'Damaged Products',
  refunds: 'Refunds',
  ndr: 'NDR',
  all: 'All Fields',
};

// Inline edit for an admin's mobile/WhatsApp number. The server normalizes
// it (bare 10 digits -> +91…) and returns the stored form; an empty value
// clears it.
function AdminMobileCell({ admin, authFetch, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(admin.mobile_number || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`/api/admins/${admin.id}/mobile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobileNumber: value.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save mobile number');
      setEditing(false);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="admin-mobile-cell">
        <span>{admin.mobile_number || <span className="admin-mobile-empty">Not set</span>}</span>
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            setValue(admin.mobile_number || '');
            setError('');
            setEditing(true);
          }}
        >
          {admin.mobile_number ? 'Edit' : 'Add'}
        </button>
      </span>
    );
  }
  return (
    <form className="admin-mobile-form" onSubmit={save}>
      <input
        type="tel"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="+91 98765 43210"
        aria-label={`Mobile number for ${admin.username}`}
        autoFocus
      />
      <button type="submit" className="link-btn" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      <button type="button" className="link-btn" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
      {error && <div className="call-feedback call-feedback-error">{error}</div>}
    </form>
  );
}

function ManageAdmins({ calls = [] }) {
  const { authFetch } = useAuth();
  const [view, setView] = useState('admins'); // 'admins' | 'audit'
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const knownAgents = getKnownAgents(calls);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [agentNumber, setAgentNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [savingAgentId, setSavingAgentId] = useState(null);
  const [uploadingAvatarId, setUploadingAvatarId] = useState(null);
  const [avatarError, setAvatarError] = useState('');

  const [roleName, setRoleName] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [adminsRes, rolesRes, permsRes] = await Promise.all([
        authFetch('/api/admins'),
        authFetch('/api/roles'),
        authFetch('/api/permissions'),
      ]);
      const [adminsData, rolesData, permsData] = await Promise.all([
        adminsRes.json(),
        rolesRes.json(),
        permsRes.json(),
      ]);
      setAdmins(Array.isArray(adminsData) ? adminsData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setPermissionOptions(Array.isArray(permsData.permissions) ? permsData.permissions : []);
    } catch (err) {
      console.error('Error fetching admins/roles:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetRoleForm = () => {
    setEditingRoleId(null);
    setRoleName('');
    setRolePermissions([]);
    setRoleError('');
  };

  const togglePermission = (key) => {
    setRolePermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handleEditRole = (role) => {
    setEditingRoleId(role.id);
    setRoleName(role.name);
    setRolePermissions(role.permissions);
    setRoleError('');
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    setRoleError('');
    setSavingRole(true);
    try {
      const url = editingRoleId ? `/api/roles/${editingRoleId}` : '/api/roles';
      const method = editingRoleId ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roleName, permissions: rolePermissions }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save role');
      resetRoleForm();
      await fetchAll();
    } catch (err) {
      setRoleError(err.message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (id, name) => {
    const confirmed = window.confirm(
      `Delete role "${name}"? Any admin using it will lose its page access until a new role is assigned.`
    );
    if (!confirmed) return;
    try {
      await authFetch(`/api/roles/${id}`, { method: 'DELETE' });
      if (editingRoleId === id) resetRoleForm();
      await fetchAll();
    } catch (err) {
      console.error('Error deleting role:', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const agent = knownAgents.find((a) => a.number === agentNumber);
      const res = await authFetch('/api/admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          roleId: roleId ? Number(roleId) : null,
          agentNumber: agent?.number || null,
          agentName: agent?.name || null,
          mobileNumber: mobileNumber.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create admin');
      setUsername('');
      setPassword('');
      setRoleId('');
      setAgentNumber('');
      setMobileNumber('');
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Called from the admin list to (re)link, or unlink, which agent identity
  // logging in as this account should default the call-picker to.
  const handleAgentChange = async (adminId, number) => {
    setSavingAgentId(adminId);
    try {
      const agent = knownAgents.find((a) => a.number === number);
      await authFetch(`/api/admins/${adminId}/agent`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentNumber: agent?.number || null, agentName: agent?.name || null }),
      });
      await fetchAll();
    } catch (err) {
      console.error('Error updating admin agent link:', err);
    } finally {
      setSavingAgentId(null);
    }
  };

  const handleAvatarUpload = async (adminId, file) => {
    if (!file) return;
    setAvatarError('');
    setUploadingAvatarId(adminId);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await authFetch(`/api/admins/${adminId}/avatar`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload photo');
      await fetchAll();
    } catch (err) {
      setAvatarError(err.message);
    } finally {
      setUploadingAvatarId(null);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = window.confirm(`Remove admin "${name}"? They will no longer be able to log in.`);
    if (!confirmed) return;
    try {
      await authFetch(`/api/admins/${id}`, { method: 'DELETE' });
      await fetchAll();
    } catch (err) {
      console.error('Error deleting admin:', err);
    }
  };

  return (
    <div>
      <div className="customer-toggle" role="group" aria-label="Manage Admins view">
        <button type="button" className={view === 'admins' ? 'active' : ''} onClick={() => setView('admins')}>
          Admins &amp; Roles
        </button>
        <button type="button" className={view === 'audit' ? 'active' : ''} onClick={() => setView('audit')}>
          Audit Log
        </button>
      </div>

      {view === 'audit' ? (
        <AuditLog authFetch={authFetch} />
      ) : (
        <>
      <h2 className="agent-page-title">Roles</h2>
      <form className="admin-create-form" onSubmit={handleSaveRole}>
        <input
          type="text"
          placeholder="Role name (e.g. Supervisor)"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          required
        />
        <div className="role-permission-checkboxes">
          {permissionOptions.map((key) => (
            <label key={key} className="role-permission-checkbox">
              <input
                type="checkbox"
                checked={rolePermissions.includes(key)}
                onChange={() => togglePermission(key)}
              />
              {PERMISSION_LABELS[key] || key}
            </label>
          ))}
        </div>
        <button type="submit" className="btn btn-primary" disabled={savingRole}>
          {savingRole ? 'Saving…' : editingRoleId ? 'Save role' : 'Create role'}
        </button>
        {editingRoleId && (
          <button type="button" className="btn btn-secondary" onClick={resetRoleForm}>
            Cancel
          </button>
        )}
      </form>
      {roleError && <div className="login-error admin-create-error">{roleError}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : roles.length === 0 ? (
        <p className="agent-page-empty">No roles yet. Create one above to control what admins can access.</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Access</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td className="table-cell-strong">{role.name}</td>
                  <td>
                    {role.permissions.length === 0
                      ? '—'
                      : role.permissions.map((p) => PERMISSION_LABELS[p] || p).join(', ')}
                  </td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => handleEditRole(role)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-danger-outline"
                      onClick={() => handleDeleteRole(role.id, role.name)}
                    >
                      <TrashIcon width={14} height={14} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="agent-page-title admin-list-title">Create admin account</h2>
      <form className="admin-create-form" onSubmit={handleCreate}>
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password (min 8 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          required
        />
        <input
          type="tel"
          placeholder="Mobile (WhatsApp), e.g. +91 98765 43210"
          value={mobileNumber}
          onChange={(e) => setMobileNumber(e.target.value)}
          aria-label="Mobile (WhatsApp) number"
        />
        <SelectMenu
          className="admin-form-select"
          value={roleId}
          onChange={setRoleId}
          ariaLabel="Role"
          options={[
            { value: '', label: 'No role (no page access)' },
            ...roles.map((role) => ({ value: String(role.id), label: role.name })),
          ]}
        />
        <SelectMenu
          className="admin-form-select"
          value={agentNumber}
          onChange={setAgentNumber}
          ariaLabel="Linked agent — the call picker will default to them"
          options={[
            { value: '', label: 'No linked agent' },
            ...knownAgents.map((agent) => ({ value: agent.number, label: agent.name })),
          ]}
        />
        <button type="submit" className="btn btn-primary" disabled={creating}>
          {creating ? 'Creating…' : 'Create admin'}
        </button>
      </form>
      {error && <div className="login-error admin-create-error">{error}</div>}

      <h2 className="agent-page-title admin-list-title">Admin accounts</h2>
      {avatarError && <div className="login-error admin-create-error">{avatarError}</div>}
      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : admins.length === 0 ? (
        <p className="agent-page-empty">No admin accounts yet.</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Username</th>
                <th>Role</th>
                <th>Mobile (WhatsApp)</th>
                <th>Linked agent</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id}>
                  <td>
                    <label className="admin-avatar-upload" title="Upload profile photo">
                      {admin.avatar_url ? (
                        <img src={admin.avatar_url} alt="" className="admin-avatar-thumb" />
                      ) : (
                        <span className="admin-avatar-thumb admin-avatar-placeholder">
                          {admin.username.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <span className="admin-avatar-upload-badge">
                        <CameraIcon width={11} height={11} />
                      </span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        disabled={uploadingAvatarId === admin.id}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          handleAvatarUpload(admin.id, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </td>
                  <td className="table-cell-strong">{admin.username}</td>
                  <td>{admin.role_name || 'No role'}</td>
                  <td>
                    <AdminMobileCell admin={admin} authFetch={authFetch} onSaved={fetchAll} />
                  </td>
                  <td>
                    <SelectMenu
                      className="admin-agent-select"
                      value={admin.agent_number || ''}
                      onChange={(value) => handleAgentChange(admin.id, value)}
                      disabled={savingAgentId === admin.id}
                      ariaLabel={`Linked agent for ${admin.username}`}
                      options={[
                        { value: '', label: 'No linked agent' },
                        ...knownAgents.map((agent) => ({ value: agent.number, label: agent.name })),
                      ]}
                    />
                  </td>
                  <td>{new Date(admin.created_at).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setResetTarget({ id: admin.id, username: admin.username })}
                    >
                      <KeyIcon width={14} height={14} />
                      Reset password
                    </button>
                    <button
                      className="btn btn-danger-outline"
                      onClick={() => handleDelete(admin.id, admin.username)}
                    >
                      <TrashIcon width={14} height={14} />
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resetTarget && (
        <ChangePasswordModal
          mode="admin"
          targetAdmin={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
        </>
      )}
    </div>
  );
}

export default ManageAdmins;
